/**
 * Location Permission Expiry Reminder Hook
 * 
 * Shows a toast notification when location permission is about to expire.
 * Checks every 5 minutes when permission is active.
 */

import { useState, useEffect, useCallback } from 'react';
import { getPermissionTimeRemaining, LocationPermissionChoice } from '../components/LocationPermissionDialog';
import { telegram } from './telegram';

const REMINDER_THRESHOLD_MINUTES = 30; // Show reminder at 30 min remaining
const CHECK_INTERVAL_MS = 5 * 60 * 1000; // Check every 5 minutes

interface ExpiryReminderState {
  showReminder: boolean;
  minutesRemaining: number | null;
  choice: LocationPermissionChoice | null;
  message: string;
}

export function useLocationExpiryReminder() {
  const [state, setState] = useState<ExpiryReminderState>({
    showReminder: false,
    minutesRemaining: null,
    choice: null,
    message: ''
  });
  
  const [reminderShown, setReminderShown] = useState(false);
  
  const checkExpiry = useCallback(() => {
    const { hasPermission, minutesRemaining, choice } = getPermissionTimeRemaining();
    
    if (!hasPermission || minutesRemaining === null) {
      setState({ showReminder: false, minutesRemaining: null, choice: null, message: '' });
      setReminderShown(false);
      return;
    }
    
    // Update state with current time remaining
    setState(prev => ({
      ...prev,
      minutesRemaining,
      choice
    }));
    
    // Show reminder if under threshold and not already shown
    if (minutesRemaining <= REMINDER_THRESHOLD_MINUTES && !reminderShown) {
      const durationLabel = choice === '24h' ? '24-hour' : choice === '12h' ? '12-hour' : 'session';
      const message = minutesRemaining <= 5
        ? `📍 Location access expires in ${minutesRemaining} min`
        : `📍 Your ${durationLabel} location access expires in ${minutesRemaining} min`;
      
      setState(prev => ({
        ...prev,
        showReminder: true,
        message
      }));
      
      setReminderShown(true);
      telegram.hapticFeedback('light');
    }
  }, [reminderShown]);
  
  // Check on mount and set interval
  useEffect(() => {
    checkExpiry();
    
    const interval = setInterval(checkExpiry, CHECK_INTERVAL_MS);
    
    return () => clearInterval(interval);
  }, [checkExpiry]);
  
  // Reset reminder shown flag when permission changes
  useEffect(() => {
    const { choice } = getPermissionTimeRemaining();
    if (choice !== state.choice) {
      setReminderShown(false);
    }
  }, [state.choice]);
  
  const dismissReminder = useCallback(() => {
    setState(prev => ({ ...prev, showReminder: false }));
  }, []);
  
  // Request location again - opens the permission dialog
  const requestLocationAgain = useCallback(() => {
    // Clear the reminder
    setState(prev => ({ ...prev, showReminder: false }));
    setReminderShown(false);
    
    // Dispatch event to open location permission dialog
    window.dispatchEvent(new CustomEvent('requestLocationPermission', {
      detail: { featureName: 'extend location access' }
    }));
  }, []);
  
  return {
    expiryWarning: {
      show: state.showReminder,
      minutesRemaining: state.minutesRemaining,
      choice: state.choice,
      message: state.message
    },
    dismissExpiryWarning: dismissReminder,
    requestLocationAgain
  };
}

/**
 * Get human-readable time remaining string
 */
export function formatTimeRemaining(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  }
  
  return `${hours}h ${remainingMinutes}m`;
}
